import { exportGroupNames, filterFingerprint } from './ExportCompetitorGroups';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as EmailValidator from 'email-validator';
import { CheckIcon, PlusIcon, XMarkIcon } from '@heroicons/react/20/solid';

import Modal from '@app/component/overlays/modal/Modal';
import ModalHandler from '@app/component/overlays/modal/ModalHandler';
import CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import SavedMarketStateFilter from '@app/model/market_state/_saved_filters/SavedMarketStateFilter';
import MarketStateExportFilter from '@app/model/market_state/_export/MarketStateExportFilter';
import { useFindMySavedMarketStateFilters } from '@app/model/market_state/_saved_filters/service/SavedMarketStateFilterServiceHooks';
import MarketStateExportService from '@app/model/market_state/_export/service/MarketStateExportService';
import MarketStateExportDeliverySchedule from '@app/model/market_state/_export/delivery/MarketStateExportDeliverySchedule';
import MarketStateExportDeliveryScheduleInput
	from '@app/model/market_state/_export/delivery/input/MarketStateExportDeliveryScheduleInput';
import { MarketStateExportReportPeriod } from '@app/model/market_state/_export/MarketStateExportReport';
import ReportsDeliveryScheduleState from '@app/model/review/delivery/ReportsDeliveryScheduleState';
import ReportsDeliveryScheduleType from '@app/model/review/delivery/ReportsDeliveryScheduleType';
import WeekDay from '@app/model/review/delivery/WeekDay';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import Utilities from '@util/Utilities';

type ScheduledReportDraft = {
	key: string;
	label: string;
	filter: MarketStateExportFilter;
};

type ScheduleFormState = {
	enabled: boolean;
	period: MarketStateExportReportPeriod;
	scheduleType: ReportsDeliveryScheduleType;
	weekdays: WeekDay[];
	dayOfMonth: number;
	time: string;
	recipients: string[];
	email: string;
	reports: ScheduledReportDraft[];
	error?: string;
};

const MARKET_STATE_EXPORT_DELIVERY_SCHEDULE_QUERY_KEY = 'market-state-export-delivery-schedules';

const PERIOD_OPTIONS = [
	{ value: MarketStateExportReportPeriod.CURRENT_MONTH, label: 'Current month' },
	{ value: MarketStateExportReportPeriod.MONTHS_3, label: 'Next 3 months' },
	{ value: MarketStateExportReportPeriod.MONTHS_6, label: 'Next 6 months' },
	{ value: MarketStateExportReportPeriod.MONTHS_12, label: 'Next 12 months' },
];

const WEEKDAY_OPTIONS = [
	{ value: WeekDay.MONDAY, label: 'Mon' },
	{ value: WeekDay.TUESDAY, label: 'Tue' },
	{ value: WeekDay.WEDNESDAY, label: 'Wed' },
	{ value: WeekDay.THURSDAY, label: 'Thu' },
	{ value: WeekDay.FRIDAY, label: 'Fri' },
	{ value: WeekDay.SATURDAY, label: 'Sat' },
	{ value: WeekDay.SUNDAY, label: 'Sun' },
];

const ExportToExcelScheduleReportModal: React.FunctionComponent<{
	show: boolean;
	onClose: () => void;
	companyHotel: CompanyHotel;
	currentFilter: MarketStateExportFilter;
}> = ({ show, onClose, companyHotel, currentFilter }) => {
	const modalRef = useRef<ModalHandler | null>(null);
	const queryClient = useQueryClient();
	const toaster = useToaster();
	const [state, setState] = useState<ScheduleFormState>(() => createInitialState(currentFilter));

	const queryKey = [MARKET_STATE_EXPORT_DELIVERY_SCHEDULE_QUERY_KEY, companyHotel.id];
	const schedulesQuery = useQuery({
		queryKey,
		queryFn: () => MarketStateExportService.findCompanyHotelMarketStateExportDeliverySchedules(companyHotel.id),
		enabled: show,
		refetchOnWindowFocus: false,
	});
	const { data: savedFilters } = useFindMySavedMarketStateFilters(companyHotel.id, {
		enabled: show,
		refetchOnWindowFocus: false,
	});

	const schedule = schedulesQuery.data?.[0];

	useEffect(() => {
		if (!show) return;
		setState(createInitialState(currentFilter, schedule));
	}, [
		show,
		currentFilter,
		schedule?.schedule.id,
		schedule?.schedule.state,
		schedule?.schedule.recipients?.join('|'),
		schedule?.schedule.scheduleType,
		schedule?.schedule.weekdays?.join('|'),
		schedule?.schedule.dayOfMonth,
		schedule?.schedule.time,
		schedule?.reports.map(report => report.id).join('|'),
	]);

	const addableReports = useMemo(
		() => buildAddableReports(currentFilter, savedFilters ?? [], state.reports),
		[currentFilter, savedFilters, state.reports],
	);

	const saveMutation = useMutation({
		mutationFn: (input: MarketStateExportDeliveryScheduleInput) => MarketStateExportService.saveMarketStateExportDeliverySchedule(input),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey });
			toaster.success('Schedule Report', 'Report delivery schedule saved.');
			modalRef.current?.close();
		},
		onError: () => toaster.error('Schedule Report', 'Failed to save report delivery schedule.'),
	});

	const addRecipient = () => {
		const email = state.email.trim();

		if (!EmailValidator.validate(email)) {
			setState(current => ({ ...current, error: 'Enter a valid recipient email.' }));
			return;
		}

		if (state.recipients.includes(email)) {
			setState(current => ({ ...current, email: '', error: undefined }));
			return;
		}

		setState(current => ({
			...current,
			email: '',
			error: undefined,
			recipients: [...current.recipients, email],
		}));
	};

	const removeRecipient = (email: string) => {
		setState(current => ({
			...current,
			recipients: current.recipients.filter(recipient => recipient !== email),
		}));
	};

	const addReport = (report: ScheduledReportDraft) => {
		setState(current => ({
			...current,
			error: undefined,
			reports: [...current.reports, report],
		}));
	};

	const removeReport = (key: string) => {
		setState(current => ({
			...current,
			reports: current.reports.filter(report => report.key !== key),
		}));
	};

	const toggleWeekday = (weekday: WeekDay) => {
		setState(current => {
			const hasWeekday = current.weekdays.includes(weekday);
			const weekdays = hasWeekday
				? current.weekdays.filter(item => item !== weekday)
				: [...current.weekdays, weekday];

			return { ...current, weekdays: weekdays.length ? weekdays : [weekday] };
		});
	};

	const save = () => {
		if (state.reports.length === 0) {
			setState(current => ({ ...current, error: 'Add at least one report filter.' }));
			return;
		}

		if (state.enabled && state.recipients.length === 0) {
			setState(current => ({ ...current, error: 'Add at least one recipient before enabling the schedule.' }));
			return;
		}

		saveMutation.mutate({
			id: schedule?.schedule.id,
			companyHotelId: companyHotel.id,
			enabled: state.enabled,
			period: state.period,
			recipients: state.recipients,
			time: state.time,
			scheduleType: state.scheduleType,
			weekdays: state.scheduleType === ReportsDeliveryScheduleType.WEEKLY ? state.weekdays : undefined,
			dayOfMonth: state.scheduleType === ReportsDeliveryScheduleType.MONTHLY ? state.dayOfMonth : undefined,
			reports: state.reports.map(report => ({ filter: report.filter })),
		});
	};

	return <Modal
		ref={modalRef}
		show={show}
		onClose={onClose}
		title="Schedule Report"
		subtitle="Create a scheduled email with one report attachment per selected filter."
		width="4xlarge"
		actions={<button
			type="button"
			className="btn btn-primary"
			disabled={saveMutation.isPending}
			onClick={save}
		>
			{saveMutation.isPending ? 'Saving...' : 'Save Schedule'}
		</button>}
	>
		<div className="space-y-7">
			<section>
				<SectionTitle title="Reports in this schedule" />
				<div className="space-y-3">
					{state.reports.length === 0 && (
						<p className="rounded-lg border border-dashed border-gray-200 bg-gray-50 py-6 text-center text-sm text-gray-500">
							No report filters selected.
						</p>
					)}
					{state.reports.map(report => (
						<FilterCard
                            companyHotel={companyHotel}
							key={report.key}
							report={report}
							isSelected
							actionLabel="Remove"
							onAction={() => removeReport(report.key)}
							actionIcon={<XMarkIcon className="h-4 w-4" />}
						/>
					))}
				</div>
			</section>

			<section>
				<SectionTitle title="Add reports" />
				<div className="max-h-64 space-y-3 overflow-y-auto pr-2">
					{addableReports.length === 0 && (
						<p className="rounded-lg border border-dashed border-gray-200 bg-gray-50 py-6 text-center text-sm text-gray-500">
							All available filters are already in the schedule.
						</p>
					)}
					{addableReports.map(report => (
						<FilterCard
                            companyHotel={companyHotel}
							key={report.key}
							report={report}
							actionLabel="Add"
							onAction={() => addReport(report)}
							actionIcon={<PlusIcon className="h-4 w-4" />}
						/>
					))}
				</div>
			</section>

			<section>
				<SectionTitle title="Report period" />
				<div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
					{PERIOD_OPTIONS.map(option => (
						<label key={option.value} className={radioCardClassName(state.period === option.value)}>
							<input
								type="radio"
								className="sr-only"
								checked={state.period === option.value}
								onChange={() => setState(current => ({ ...current, period: option.value }))}
							/>
							<span>{option.label}</span>
						</label>
					))}
				</div>
			</section>

			<section>
				<SectionTitle title="Schedule of report delivery" />
				<div className="grid grid-cols-1 gap-4 md:grid-cols-3">
					<div>
						<label className="label">Status</label>
						<label className="mt-1.5 flex items-center gap-2 text-sm text-gray-700">
							<input
								type="checkbox"
								className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
								checked={state.enabled}
								onChange={(event) => setState(current => ({ ...current, enabled: event.target.checked, error: undefined }))}
							/>
							Enabled
						</label>
					</div>
					<div>
						<label className="label">Frequency</label>
						<select
							className="input mt-1.5"
							value={state.scheduleType}
							onChange={(event) => setState(current => ({
								...current,
								scheduleType: event.target.value as ReportsDeliveryScheduleType,
							}))}
						>
							<option value={ReportsDeliveryScheduleType.WEEKLY}>Weekly</option>
							<option value={ReportsDeliveryScheduleType.MONTHLY}>Monthly</option>
						</select>
					</div>
					<div>
						<label className="label">Delivery time</label>
						<input
							type="time"
							className="input mt-1.5"
							value={state.time}
							onChange={(event) => setState(current => ({ ...current, time: event.target.value }))}
						/>
					</div>
				</div>

				{state.scheduleType === ReportsDeliveryScheduleType.WEEKLY && (
					<div className="mt-4">
						<label className="label">Weekdays</label>
						<div className="mt-2 flex flex-wrap gap-2">
							{WEEKDAY_OPTIONS.map(option => (
								<button
									key={option.value}
									type="button"
									className={Utilities.classNames(
										state.weekdays.includes(option.value)
											? 'bg-blue-600 text-white ring-blue-600'
											: 'bg-white text-gray-700 ring-gray-200 hover:bg-gray-50',
										'rounded-full px-3 py-1.5 text-xs font-medium ring-1',
									)}
									onClick={() => toggleWeekday(option.value)}
								>
									{option.label}
								</button>
							))}
						</div>
					</div>
				)}

				{state.scheduleType === ReportsDeliveryScheduleType.MONTHLY && (
					<div className="mt-4 max-w-xs">
						<label className="label">Day of month</label>
						<input
							type="number"
							min={1}
							max={28}
							className="input mt-1.5"
							value={state.dayOfMonth}
							onChange={(event) => setState(current => ({
								...current,
								dayOfMonth: Math.max(1, Math.min(28, Number(event.target.value) || 1)),
							}))}
						/>
						<p className="mt-1 text-xs text-gray-400">Use days 1-28 to avoid invalid month endings.</p>
					</div>
				)}
			</section>

			<section>
				<SectionTitle title="Email recipients" />
				<div className="flex flex-col gap-3 sm:flex-row">
					<input
						type="email"
						className="input flex-1"
						placeholder="name@company.com"
						value={state.email}
						onChange={(event) => setState(current => ({ ...current, email: event.target.value, error: undefined }))}
						onKeyDown={(event) => {
							if (event.key === 'Enter') {
								event.preventDefault();
								addRecipient();
							}
						}}
					/>
					<button type="button" className="btn btn-white" onClick={addRecipient}>Add</button>
				</div>

				{!!state.error && <p className="mt-2 text-xs text-red-600">{state.error}</p>}

				<div className="mt-3 min-h-16 rounded-lg border border-dashed border-gray-200 bg-gray-50 p-3">
					{state.recipients.length === 0 && (
						<p className="py-3 text-center text-sm text-gray-500">No recipients added.</p>
					)}
					{state.recipients.length > 0 && (
						<ul className="flex flex-wrap gap-2">
							{state.recipients.map(email => (
								<li
									key={email}
									className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-sm text-gray-700 ring-1 ring-gray-200"
								>
									<CheckIcon className="h-4 w-4 text-emerald-500" />
									{email}
									<button
										type="button"
										className="rounded-full p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
										onClick={() => removeRecipient(email)}
									>
										<XMarkIcon className="h-4 w-4" />
									</button>
								</li>
							))}
						</ul>
					)}
				</div>
			</section>
		</div>
	</Modal>;
};

const SectionTitle: React.FunctionComponent<{ title: string }> = ({ title }) => (
	<div className="mb-3 border-b border-gray-200 pb-2">
		<h4 className="text-sm font-semibold text-gray-900">{title}</h4>
	</div>
);

const FilterCard: React.FunctionComponent<{
	report: ScheduledReportDraft;
    companyHotel: CompanyHotel;
	isSelected?: boolean;
	actionLabel: string;
	actionIcon: React.ReactNode;
	onAction: () => void;
}> = ({ report, companyHotel, isSelected = false, actionLabel, actionIcon, onAction }) => (
	<div className={Utilities.classNames(
		isSelected ? 'border-blue-200 bg-blue-50' : 'border-gray-200 bg-white',
		'rounded-lg border px-4 py-3',
	)}>
		<div className="flex items-start justify-between gap-3">
			<div className="min-w-0">
				<p className="text-sm font-semibold text-gray-900">{report.label}</p>
				<div className="mt-2 flex flex-wrap gap-1.5">
					{buildFilterChips(report.filter, companyHotel).map((chip, index) => (
						<span key={`${index}-${chip}`} className="rounded bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600">
							{chip}
						</span>
					))}
				</div>
			</div>
			<button
				type="button"
				className="inline-flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold text-blue-600 hover:bg-white"
				onClick={onAction}
			>
				{actionIcon}
				{actionLabel}
			</button>
		</div>
	</div>
);

const createInitialState = (
	currentFilter: MarketStateExportFilter,
	schedule?: MarketStateExportDeliverySchedule,
): ScheduleFormState => {
	const reports = schedule?.reports.length
		? schedule.reports.map((report, index) => ({
			key: `report-${report.id}`,
			label: `Scheduled report ${index + 1}`,
			filter: report.filter,
		}))
		: [{
			key: `current-${filterFingerprint(currentFilter)}`,
			label: 'Current Filters',
			filter: currentFilter,
		}];

	const period = schedule?.reports.find(report => report.period !== MarketStateExportReportPeriod.CUSTOM_DATES)?.period
		?? MarketStateExportReportPeriod.CURRENT_MONTH;

	return {
		enabled: schedule?.schedule.state ? schedule.schedule.state === ReportsDeliveryScheduleState.ACTIVE : true,
		period,
		scheduleType: schedule?.schedule.scheduleType ?? ReportsDeliveryScheduleType.WEEKLY,
		weekdays: schedule?.schedule.weekdays?.length ? schedule.schedule.weekdays : [WeekDay.MONDAY],
		dayOfMonth: schedule?.schedule.dayOfMonth ?? 1,
		time: schedule?.schedule.time ?? '09:00',
		recipients: schedule?.schedule.recipients ?? [],
		email: '',
		reports,
	};
};

const buildAddableReports = (
	currentFilter: MarketStateExportFilter,
	savedFilters: SavedMarketStateFilter[],
	selectedReports: ScheduledReportDraft[],
): ScheduledReportDraft[] => {
	const selectedFingerprints = new Set(selectedReports.map(report => filterFingerprint(report.filter)));
	const options: ScheduledReportDraft[] = [
		{
			key: `current-${filterFingerprint(currentFilter)}`,
			label: 'Current Filters',
			filter: currentFilter,
		},
		...savedFilters.map(savedFilter => ({
			key: `saved-${savedFilter.id}`,
			label: savedFilter.name,
			filter: savedFilter.filter,
		})),
	];

	return options.filter(option => !selectedFingerprints.has(filterFingerprint(option.filter)));
};

const buildFilterChips = (filter: MarketStateExportFilter, companyHotel: CompanyHotel): string[] => {
	const chips: string[] = exportGroupNames(filter.competitorTagIds, companyHotel.competitorTags).map(name => `Group: ${name}`);

	if (filter.ratesType) chips.push(String(filter.ratesType));
	if (filter.roomTypes?.length) chips.push(filter.roomTypes.map(roomType => String(roomType)).join(', '));
	if (filter.provider) chips.push(String(filter.provider));
	if (typeof filter.nights === 'number') chips.push(`${filter.nights} ${filter.nights === 1 ? 'night' : 'nights'}`);
	if (typeof filter.guests === 'number') chips.push(`${filter.guests} ${filter.guests === 1 ? 'guest' : 'guests'}`);
	if (filter.meal) chips.push(String(filter.meal));
	if (filter.refunding) chips.push(String(filter.refunding));
	if (filter.platform) chips.push(String(filter.platform));
	if (filter.region) chips.push(String(filter.region));
	if (filter.airportCode) chips.push(String(filter.airportCode));

	return chips.length ? chips : ['Any filter'];
};

const radioCardClassName = (isActive: boolean): string => Utilities.classNames(
	isActive ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50',
	'cursor-pointer rounded-lg border px-3 py-3 text-sm font-medium',
);

export default ExportToExcelScheduleReportModal;
